import { useTranslation, Trans } from 'next-i18next'
import { Fragment, useEffect, useMemo, useState } from 'react'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import Button from '~/components/Buttons/Button'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { selectHasShownLeaderboardModal } from '~/gamification/slices/gamification.slice'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetBadgeGradient } from '~/hooks/useGetLeagueInfo'
import { useGetLeaderboardDataQuery } from '~/Leaderboard/apis/leaderboard-api'
import CurrentLeaderboard from '~/Leaderboard/components/CurrentLeaderboard'
import { LeaderboardData, LeagueType } from '~/Leaderboard/types/leaderboard.types'
import { formatTimeFromSec } from '~/Leaderboard/utils/utils'
import { useAppSelector } from '~/redux/hooks'

export default function LeagueModal({
  prevPos,
  currentPos,
  onCloseLeagueModal,
}: {
  prevPos: number
  currentPos: number
  onCloseLeagueModal: () => void
}): React.ReactElement {
  const { t } = useTranslation(['leaderboard', 'bootcamp'])
  const authenticated = useAppSelector(isAuthenticated)
  const hasShownLeaderboardModal = useAppSelector(selectHasShownLeaderboardModal)
  const [timeToReset, setTimeToReset] = useState(0)

  const timeText = { day: t('days'), hour: t('hours'), min: t('minutes') }
  const formatTime = formatTimeFromSec({ seconds: timeToReset, timeText })

  const user = useCurrentUser()
  const userId = user?.id || -1
  const { data, isFetching } = useGetLeaderboardDataQuery(undefined, { skip: !authenticated })

  // One settled snapshot drives the whole modal. The set's XP invalidates this query as the modal
  // opens and RTK Query serves the pre-award board while refetching, so reading live data here put
  // the rows (frozen at CurrentLeaderboard's mount) and the "XP to next position" line on two
  // different fetches.
  const [board, setBoard] = useState<LeaderboardData | undefined>(undefined)
  useEffect(() => {
    if (isFetching || !data) return
    setBoard((snapshot) => snapshot ?? data)
  }, [data, isFetching])

  const users = board?.users || []
  const currentUserIndex = users.findIndex((participant) => participant.id === userId)
  // Rank comes from the same snapshot as the rows, not from the prop, so the two cannot disagree.
  const currentRank = currentUserIndex > -1 ? currentUserIndex + 1 : currentPos
  // null means there is nobody to catch: rank 1, or the user is not on this board.
  const xpToGo = currentUserIndex > 0 ? users[currentUserIndex - 1].xp - users[currentUserIndex].xp : null

  const userIsMovingUp = currentRank < prevPos
  const animate = useMemo(() => ({ from: prevPos, to: currentRank }), [prevPos, currentRank])

  // The animation's starting board: the user parked at their previous rank, everyone else in place.
  const leagueData = useMemo((): LeaderboardData | undefined => {
    if (!board || currentUserIndex === -1 || prevPos <= currentRank) return board
    const reordered = [...board.users]
    // only mutating a copy of the array
    // eslint-disable-next-line fp/no-mutating-methods
    reordered.splice(currentUserIndex, 1)
    const validPrevPos = Math.max(0, Math.min(prevPos - 1, reordered.length))
    // eslint-disable-next-line fp/no-mutating-methods
    reordered.splice(validPrevPos, 0, board.users[currentUserIndex])
    return { ...board, users: reordered }
  }, [board, currentRank, currentUserIndex, prevPos])

  const currentLeague: number = board?.currentLeague || 1
  const currentLeagueName: string = LeagueType[currentLeague].toLowerCase()
  const leagueIconUrl: string = `gamification/badges/league/${currentLeagueName}-nav-badge.${
    currentLeague === 5 ? 'svg' : 'png'
  }`
  const badgeColor = useGetBadgeGradient(currentLeague)

  useEffect(() => {
    if (!board) return
    setTimeToReset(board.seasonEndInSec)
  }, [board])

  useEffect(() => {
    if (timeToReset <= 0) return
    const interval = setInterval(() => {
      setTimeToReset((prevState) => prevState - 20)
    }, 20000)
    return () => clearInterval(interval)
  })

  if (!board) return <Fragment />
  if (hasShownLeaderboardModal && !userIsMovingUp) {
    return <Fragment />
  }
  return (
    <div className="absolute w-screen left-0 right-0 mx-auto">
      <button
        className="fixed overflow-auto top-0 bottom-0 left-0 right-0 w-full flex flex-col items-center justify-center bg-black bg-opacity-75 z-top"
        onClick={onCloseLeagueModal}
      >
        <div className="flex-1 md:flex-none w-full md:w-auto min-h-0 overflow-y-auto util-scrollbar shop-scrollbar bg-purple-darker text-white p-4 md:rounded">
          <div className="flex items-center justify-center gap-4">
            <div className="font-bold">
              <h3 className="text-h3">{t('leaderboard:yourRank')}</h3>
              <p>
                {xpToGo === null || xpToGo > 1000 || xpToGo < 0
                  ? Trans({
                      t,
                      i18nKey: 'leaderboard:timeEnds',
                      components: { span: <span className="text-orange" /> },
                      children: t('leaderboard:timeEnds'),
                      values: { time: formatTime },
                    })
                  : Trans({
                      t,
                      i18nKey: 'leaderboard:xpToNext',
                      components: { span: <span className="text-orange" /> },
                      children: t('leaderboard:xpToNext'),
                      values: { xp: xpToGo },
                    })}
              </p>
            </div>
            <div className="relative flex items-center justify-center">
              <ResponsiveImage
                alt={currentLeagueName}
                className="absolute"
                fit="contain"
                height={50}
                imageClassName="min-w-12"
                imageWidths="50"
                srcKey={leagueIconUrl}
                width={50}
              />
              <p className={`z-10 text-lg font-bold bg-clip-text text-transparent mb-0.5 ${badgeColor}`}>
                <span className="text-md"># </span>
                {currentRank}
              </p>
            </div>
          </div>
          {/* CurrentLeaderboard copies the board into state once, so remount it when either end changes: the host
              can update the previous rank while the modal is open, and a list parked at the old start rank then
              animated the wrong rows on top of each other. */}
          <CurrentLeaderboard
            animate={animate}
            key={`${animate.from}-${animate.to}`}
            leaderboardData={leagueData}
            user={user}
          />
        </div>
        <div className="flex justify-center left-0 right-0 w-full shrink-0 bg-black bg-opacity-75 z-top py-4">
          <Button
            className="w-40 h-12 text-white bg-purple hover:scale-110 duration-300 cursor-pointer"
            onClick={onCloseLeagueModal}
          >
            {t('bootcamp:continue')}
          </Button>
        </div>
      </button>
    </div>
  )
}
